import {AsyncPipe} from '@angular/common';
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {FormBuilder, ReactiveFormsModule, Validators} from '@angular/forms';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatSelectModule} from '@angular/material/select';
import {MatButtonModule} from '@angular/material/button';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {MatNativeDateModule} from '@angular/material/core';
import {MatIconModule} from '@angular/material/icon';
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatGoogleMapsAutocompleteModule} from '@angular-material-extensions/google-maps-autocomplete';
import {forkJoin, Observable, of} from 'rxjs';
import {map, switchMap} from 'rxjs/operators';
import {LoadingService} from '@ui-kit/framework/util/service/loading.service';
import {SnackbarService} from '@ui-kit/framework/util/service/snackbar.service';
import {AddressService} from '@ui-kit/framework/util/service/address.service';
import {toAdminAddress} from '../../admin-address.util';
import {LibDateUtilsService} from '@ui-kit/framework/util/service/date-utils.service';
import {OfferingTypesService} from '../../offering-types/offering-types.service';
import {OfferingSetupStatus, OfferingType} from '../setup.model';
import {SetupService} from '../setup.service';
import PlaceResult = google.maps.places.PlaceResult;

/**
 * Static fallback Type catalogue (code + label), used only when the location
 * offering-types endpoint returns nothing or errors. Normally the Type select is
 * populated from that endpoint's {@code availableTypes} (platform_offering_types,
 * type OFFERING), filtered to the location's enabled types (tech spec §4.16).
 */
export const OFFERING_TYPE_OPTIONS: {code: OfferingType; label: string}[] = [
  {code: 'DISTRICT', label: 'District'},
  {code: 'EXHIBITIONS', label: 'Exhibitions'},
  {code: 'GENERAL_LEASING', label: 'General Leasing'},
  {code: 'KIOSK', label: 'Kiosk'},
  {code: 'ON_DEMAND', label: 'On Demand'},
];

const SEED_LABELS: Record<keyof OfferingSetupStatus, string> = {
  recommendationConfig: 'Recommendation Config',
  salesImportConfig: 'Sales Imports',
  productImportConfig: 'Product Imports',
  jobConfig: 'Job Schedules',
  applicationForm: 'Application Form',
};

// Unlike the other seeds this one is not fixed by saving a settings tab, but it IS
// retryable: the Readiness page's "Generate form" action re-runs the seed (idempotent).
// The earlier copy sent staff to the console Form Builder to rebuild it by hand.
const APPLICATION_FORM_GUIDANCE =
  'The application form could not be created automatically. ' +
  'Use "Generate form" on the offering\'s Readiness page to retry before retailers can apply.';

export function failedSeedLabels(setupStatus: OfferingSetupStatus): string[] {
  return (Object.keys(SEED_LABELS) as (keyof OfferingSetupStatus)[])
    .filter((key) => setupStatus[key] === 'FAILED')
    .map((key) => SEED_LABELS[key]);
}

@Component({
  selector: 'app-create-offering-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatIconModule,
    MatTooltipModule,
    MatGoogleMapsAutocompleteModule,
  ],
  templateUrl: './create-offering-dialog.component.html',
})
export class CreateOfferingDialogComponent implements OnInit {
  private readonly dialogRef = inject(MatDialogRef<CreateOfferingDialogComponent>);
  private readonly data = inject<{ locationUuid: string }>(MAT_DIALOG_DATA);
  private readonly fb = inject(FormBuilder);
  private readonly setupService = inject(SetupService);
  private readonly offeringTypesService = inject(OfferingTypesService);
  private readonly addressService = inject(AddressService);
  private readonly dateUtils = inject(LibDateUtilsService);
  private readonly snackbar = inject(SnackbarService);
  private readonly cdr = inject(ChangeDetectorRef);
  protected readonly loader = inject(LoadingService);

  protected form = this.fb.group({
    name: ['', Validators.required],
    code: ['', Validators.required],
    type: ['DISTRICT', Validators.required],
    startDate: [null as Date | null, Validators.required],
    endDate: [null as Date | null],
    address: ['', Validators.required],
    description: [''],
  });

  private place: PlaceResult | null = null;
  private geometry: any = null;

  protected tileImageFile: File | null = null;
  protected bannerImageFile: File | null = null;

  /** Static catalogue fallback; the live list comes from the location offering-types endpoint on init. */
  protected typeOptions: {code: string; label: string}[] = OFFERING_TYPE_OPTIONS;

  ngOnInit(): void {
    this.loadOfferingTypes();
  }

  /**
   * Loads the location's offering types (BE-14 / task-4) and populates the Type
   * select from {@code location_enabled_offerings}: the list is the location's
   * enabled types. An EMPTY enabled set means unrestricted — default to every
   * available OFFERING type. Codes and labels both come from the endpoint's
   * {@code availableTypes} (platform_offering_types, type OFFERING). A failed read
   * must never block offering creation, so on error we fall back to the static
   * catalogue (recorded decision, FE-20).
   */
  private loadOfferingTypes(): void {
    this.offeringTypesService.getOfferingTypes(this.data.locationUuid).subscribe({
      next: (res) => {
        const available = (res.availableTypes ?? []).map(t => ({code: t.code, label: t.name}));
        const enabledCodes = res.enabledTypeCodes ?? [];
        let options = available;
        if (enabledCodes.length) {
          const enabledSet = new Set(enabledCodes.map(c => c.toUpperCase()));
          options = available.filter(o => enabledSet.has(o.code.toUpperCase()));
        }
        // availableTypes should always be populated; only fall back if the backend returns nothing.
        this.typeOptions = options.length ? options : OFFERING_TYPE_OPTIONS;
        if (!this.typeOptions.some(o => o.code === this.form.controls.type.value)) {
          this.form.controls.type.setValue(this.typeOptions[0]?.code ?? '');
        }
        this.cdr.markForCheck();
      },
      error: () => {
        this.typeOptions = OFFERING_TYPE_OPTIONS;
        this.cdr.markForCheck();
      },
    });
  }

  generateCode(): void {
    const name = this.form.controls.name.value ?? '';
    this.form.controls.code.setValue(
      name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
    );
  }

  onTileImageSelected(event: Event): void {
    this.tileImageFile = (event.target as HTMLInputElement).files?.[0] ?? null;
  }

  onBannerImageSelected(event: Event): void {
    this.bannerImageFile = (event.target as HTMLInputElement).files?.[0] ?? null;
  }

  clearTileImage(): void {
    this.tileImageFile = null;
  }

  clearBannerImage(): void {
    this.bannerImageFile = null;
  }

  onAutocompleteSelected(place: PlaceResult): void {
    this.place = place;
  }

  onLocationSelected(location: any): void {
    this.geometry = location;
  }

  onSubmit(): void {
    if (this.form.invalid || !this.place || !this.geometry) {
      this.form.markAllAsTouched();
      return;
    }

    const v = this.form.getRawValue();
    const {address, addressPlace} = toAdminAddress(this.addressService.parseJsonAddressData(this.geometry, this.place));

    this.setupService.createOffering(this.data.locationUuid, {
      name: v.name!,
      code: v.code!,
      type: v.type as OfferingType,
      startDate: this.dateUtils.displayShortDate(v.startDate)!,
      endDate: this.dateUtils.displayShortDate(v.endDate ?? null) ?? undefined,
      address,
      addressPlace,
      description: v.description || undefined,
    })
      .pipe(
        switchMap((response) => {
          const uploads: Observable<void>[] = [];
          if (this.tileImageFile) uploads.push(this.setupService.uploadOfferingTileImage(response.uuid, this.tileImageFile));
          if (this.bannerImageFile) uploads.push(this.setupService.uploadOfferingBannerImage(response.uuid, this.bannerImageFile));
          return uploads.length ? forkJoin(uploads).pipe(map(() => response)) : of(response);
        }),
        this.loader.withLoader()
      )
      .subscribe({
        next: (response) => {
          const failed = failedSeedLabels(response.setupStatus);
          if (failed.length) {
            let message = `Offering created, but some defaults could not be created: ${failed.join(', ')}. Open the corresponding settings tab and Save to complete setup.`;
            if (response.setupStatus.applicationForm === 'FAILED') {
              message += ` ${APPLICATION_FORM_GUIDANCE}`;
            }
            this.snackbar.openSnackBar(message);
          }
          this.dialogRef.close(response.uuid);
        },
        error: () => this.snackbar.openSnackBar('Failed to create offering. Please try again.'),
      });
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
